import React, { useState } from "react";
import { buildApiUrl } from "../utils/ApiConfig";
import { getEmojiForScore } from "../utils/EmojiUtils";
import RestoApiService from "../utils/RestoApiService";
import AnalyticsManager from "../utils/AnalyticsManager";

// Props for the ReportListItem component
export interface ReportListItemProps {
  id: string;
  username: string;
  date: string;
  comment: string;
  rating: number;
  avatarColor: string;
  imageUrl?: string;
  confirmations?: number;
}

// Helper function to format date to be more readable
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
};

const ReportListItem: React.FC<ReportListItemProps> = ({ id, username, date, comment, rating, imageUrl, confirmations: initialConfirmations = 0 }) => {
  // State for tracking if text is expanded
  const [isExpanded, setIsExpanded] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [confirmCount, setConfirmCount] = useState(initialConfirmations);
  const [hasConfirmed, setHasConfirmed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const confirmed = localStorage.getItem('confirmed_reports');
    return confirmed ? JSON.parse(confirmed).includes(id) : false;
  });
  const [hasFlagged, setHasFlagged] = useState(() => {
    if (typeof window === 'undefined') return false;
    const flagged = localStorage.getItem('flagged_reports');
    return flagged ? JSON.parse(flagged).includes(id) : false;
  });
  const [isHiddenByFlags, setIsHiddenByFlags] = useState(() => {
    if (typeof window === 'undefined') return false;
    const flagCounts: Record<string, number> = JSON.parse(localStorage.getItem('flagged_report_counts') || '{}');
    return (flagCounts[id] || 0) >= 3;
  });

  const handleConfirm = async () => {
    if (hasConfirmed) return;
    const reportIdNum = parseInt(id, 10);
    if (isNaN(reportIdNum)) return;
    const api = new RestoApiService();
    const result = await api.confirmReport(reportIdNum);
    if (result.success && result.confirmations !== undefined) {
      setConfirmCount(result.confirmations);
      setHasConfirmed(true);
      const confirmed = JSON.parse(localStorage.getItem('confirmed_reports') || '[]');
      confirmed.push(id);
      localStorage.setItem('confirmed_reports', JSON.stringify(confirmed));
      AnalyticsManager.getInstance().trackEvent('report_confirmed', { report_id: id, event_category: 'engagement' });
    }
  };

  const handleFlag = () => {
    if (hasFlagged) return;
    // Mark as flagged in this session
    setHasFlagged(true);
    const flagged = JSON.parse(localStorage.getItem('flagged_reports') || '[]');
    flagged.push(id);
    localStorage.setItem('flagged_reports', JSON.stringify(flagged));
    // Increment the cross-session flag count
    const flagCounts: Record<string, number> = JSON.parse(localStorage.getItem('flagged_report_counts') || '{}');
    const newCount = (flagCounts[id] || 0) + 1;
    flagCounts[id] = newCount;
    localStorage.setItem('flagged_report_counts', JSON.stringify(flagCounts));
    // Auto-hide if 3+ flags
    if (newCount >= 3) {
      setIsHiddenByFlags(true);
    }
    AnalyticsManager.getInstance().trackEvent('report_flagged', { report_id: id, flag_count: newCount, event_category: 'moderation' });
  };

  // Check if text is long enough to need truncation
  const needsTruncation = comment.length > 150;
  
  // Build full image URL if imageUrl is provided
  const fullImageUrl = imageUrl ? buildApiUrl(imageUrl) : null;

  // Hide report if it has been flagged 3+ times across sessions
  if (isHiddenByFlags) {
    return null;
  }

  return (
    <div id={`report-${id}`} key={id} className="p-4 bg-gray-100/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200/50 dark:border-gray-700/50 hover:border-gray-300/50 dark:hover:border-gray-600/50">
      <div className="flex flex-col">
        {/* Header with username, rating, and date */}
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700 dark:text-gray-200">{username}</span>
            <div className="text-xl transform transition-transform duration-200 hover:scale-110">{getEmojiForScore(rating)}</div>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(date)}</span>
        </div>

        {/* Comment text with truncation - aligned to the left */}
        <div className="text-left text-gray-500 dark:text-gray-300 text-sm">
          <p className={`${!isExpanded && needsTruncation ? "line-clamp-3" : ""} transition-all duration-300`}>
            {comment}
          </p>

          {/* Read more / Read less button */}
          {needsTruncation && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs mt-1 focus:outline-none"
            >
              {isExpanded ? "Read less" : "Read more"}
            </button>
          )}
        </div>

        {/* Display image if available */}
        {fullImageUrl && (
          <div className="mt-3 relative">
            {isImageLoading && (
              <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse flex items-center justify-center">
                <svg className="animate-spin h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
            {imageError ? (
              <div className="bg-gray-200/50 dark:bg-gray-700/50 rounded-xl p-4 flex flex-col items-center justify-center h-32">
                <svg className="w-8 h-8 text-gray-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                <p className="text-xs text-gray-500">Image unavailable</p>
              </div>
            ) : (
              <div className="relative group overflow-hidden rounded-xl cursor-pointer">
                <img
                  src={fullImageUrl}
                  alt="Report attachment"
                  className={`w-full max-w-sm object-cover transition-all duration-300 group-hover:scale-105 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
                  onClick={() => window.open(fullImageUrl, '_blank')}
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => {
                    setIsImageLoading(false);
                    setImageError(true);
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                  </svg>
                  <span className="text-xs">View</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Confirm & Flag buttons */}
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={handleConfirm}
            disabled={hasConfirmed}
            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors ${
              hasConfirmed
                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 cursor-default'
                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400'
            }`}
            aria-label="Confirm this report"
          >
            <svg className="w-3.5 h-3.5" fill={hasConfirmed ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
            </svg>
            {hasConfirmed ? (confirmCount > 1 ? `Confirmed (${confirmCount})` : 'Confirmed') : (confirmCount > 0 ? `Confirm (${confirmCount})` : 'Confirm')}
          </button>
          <button
            onClick={handleFlag}
            disabled={hasFlagged}
            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors ${
              hasFlagged
                ? 'bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 cursor-default'
                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-500 dark:hover:text-red-400'
            }`}
            aria-label="Flag this report"
          >
            <svg className="w-3.5 h-3.5" fill={hasFlagged ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-16m0 0l2.643.858C7.076 6.203 8.51 6.5 10 6.5c1.49 0 2.924-.297 4.357-.642L17 5v8l-2.643.858C12.924 14.203 11.49 14.5 10 14.5c-1.49 0-2.924-.297-4.357-.642L3 13" />
            </svg>
            {hasFlagged ? 'Flagged' : 'Flag'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportListItem;
